import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { AdvancedDatepickerComponent } from '@shared/components/advanced-datepicker/advanced-datepicker.component';
import { PermissionService } from '@app/core/services/permission.service';
import { FlashSaleService } from './services/flash-sale.service';
import type {
  FlashSaleAlert,
  FlashSaleCampaign,
  FlashSaleScopeType,
} from '@app/api/price-service/flash-sale-api.service';

interface LineDraft {
  skuCode: string | null;
  skuName?: string;
  amount: number | null;
  quota: number | null;
  currentStock: number | null;
  maxAllowedQuota: number | null;
}

@Component({
  selector: 'app-flash-sale',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TranslateModule,
    ToastModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    InputNumberModule,
    AdvancedDatepickerComponent,
    TagModule,
  ],
  providers: [MessageService],
  templateUrl: './flash-sale.component.html',
  styleUrl: './flash-sale.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlashSaleComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly flashSaleService = inject(FlashSaleService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly rows = signal<FlashSaleCampaign[]>([]);
  readonly alerts = signal<FlashSaleAlert[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly dialogVisible = signal(false);
  readonly editingId = signal<number | null>(null);
  readonly detail = signal<FlashSaleCampaign | null>(null);
  readonly skuOptions = signal<{ skuCode: string; skuName: string }[]>([]);
  readonly shopOptions = signal<{ id: number; name: string }[]>([]);
  readonly maxStockPercent = signal(50);
  readonly formTick = signal(0);
  /** Drives live countdown column refresh every second. */
  readonly nowMs = signal(Date.now());
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  campaignName = '';
  scopeType: FlashSaleScopeType = 'ALL_STORES_AND_WEB';
  selectedShopIds: number[] = [];
  startAt: Date | null = null;
  endAt: Date | null = null;
  lines: LineDraft[] = [];

  readonly minSelectableDate = (() => {
    const date = new Date();
    date.setSeconds(0, 0);
    return date;
  })();

  readonly scopeOptions = computed(() => [
    {
      label: this.translate.instant('price_list.scope.all_stores_and_web'),
      value: 'ALL_STORES_AND_WEB' as FlashSaleScopeType,
    },
    {
      label: this.translate.instant('flash_sale.scope.selected'),
      value: 'SELECTED_STORES' as FlashSaleScopeType,
    },
  ]);

  readonly canSave = computed(() => {
    this.formTick();
    const validLines = this.lines.filter(
      (line) =>
        !!line.skuCode &&
        line.amount != null &&
        line.amount >= 0 &&
        line.quota != null &&
        line.quota >= 1,
    );
    if (!this.campaignName.trim() || !this.startAt || !this.endAt || validLines.length === 0) {
      return false;
    }
    if (
      this.editingId() == null &&
      this.startAt.getTime() < this.minSelectableDate.getTime() - 60_000
    ) {
      return false;
    }
    if (this.endAt.getTime() <= this.startAt.getTime()) {
      return false;
    }
    if (this.scopeType === 'SELECTED_STORES' && this.selectedShopIds.length === 0) {
      return false;
    }
    return !this.saving();
  });

  readonly canCreate = () =>
    this.permissionService.canDoAction('marketing.flash_sale.create');
  readonly canEdit = () =>
    this.permissionService.canDoAction('marketing.flash_sale.edit');
  readonly canDelete = () =>
    this.permissionService.canDoAction('marketing.flash_sale.delete');

  ngOnInit(): void {
    this.reload();
    this.loadAlerts();
    this.countdownTimer = setInterval(() => this.nowMs.set(Date.now()), 1000);
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.loadDetail(Number(id));
      } else {
        this.detail.set(null);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.countdownTimer != null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  reload(): void {
    this.loading.set(true);
    this.flashSaleService
      .listCampaigns()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.rows.set(rows ?? []);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toast('error', 'flash_sale.messages.load_failed');
        },
      });
  }

  loadDetail(id: number): void {
    this.flashSaleService
      .getCampaign(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (campaign) => this.detail.set(campaign),
        error: () => this.toast('error', 'flash_sale.messages.load_failed'),
      });
  }

  loadAlerts(): void {
    this.flashSaleService
      .listAlerts(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (alerts) => this.alerts.set(alerts ?? []),
        error: () => this.alerts.set([]),
      });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.resetForm();
    this.dialogVisible.set(true);
    this.loadOptions();
    this.bumpForm();
  }

  openEdit(row: FlashSaleCampaign): void {
    this.editingId.set(row.id);
    this.campaignName = row.campaignName;
    this.scopeType = row.scopeType;
    this.selectedShopIds = (row.stores ?? []).map((s) => s.physicalShopId);
    this.startAt = row.startAt ? new Date(row.startAt) : null;
    this.endAt = row.endAt ? new Date(row.endAt) : null;
    this.lines = (row.lines ?? []).map((line) => ({
      skuCode: line.skuCode,
      skuName: line.skuName ?? undefined,
      amount: line.amount,
      quota: line.quotaTotal ?? line.quota ?? null,
      currentStock: line.currentStock ?? null,
      maxAllowedQuota: line.maxAllowedQuota ?? null,
    }));
    this.dialogVisible.set(true);
    this.loadOptions();
    this.refreshStock();
    this.bumpForm();
  }

  openDetail(row: FlashSaleCampaign): void {
    this.router.navigate(['/marketing/flash-sale', row.id]);
  }

  closeDialog(): void {
    this.dialogVisible.set(false);
  }

  bumpForm(): void {
    this.formTick.update((n) => n + 1);
  }

  addLine(): void {
    this.lines = [
      ...this.lines,
      { skuCode: null, amount: null, quota: null, currentStock: null, maxAllowedQuota: null },
    ];
    this.bumpForm();
  }

  removeLine(index: number): void {
    this.lines = this.lines.filter((_, i) => i !== index);
    this.refreshStock();
    this.bumpForm();
  }

  onScopeChange(): void {
    if (this.scopeType !== 'SELECTED_STORES') {
      this.selectedShopIds = [];
    }
    this.refreshStock();
    this.bumpForm();
  }

  onScopeOrSkuChange(): void {
    this.refreshStock();
    this.bumpForm();
  }

  save(): void {
    if (
      this.editingId() == null &&
      this.startAt &&
      this.startAt.getTime() < this.minSelectableDate.getTime() - 60_000
    ) {
      this.toast('warn', 'flash_sale.messages.start_not_past');
      return;
    }
    if (this.startAt && this.endAt && this.endAt.getTime() <= this.startAt.getTime()) {
      this.toast('warn', 'flash_sale.messages.end_before_start');
      return;
    }
    if (!this.canSave()) {
      this.toast('warn', 'flash_sale.messages.required');
      return;
    }
    const validLines = this.lines.filter(
      (line) => line.skuCode && line.amount != null && line.quota != null && line.quota >= 1,
    );
    const body = {
      campaignName: this.campaignName.trim(),
      scopeType: this.scopeType,
      physicalShopIds:
        this.scopeType === 'SELECTED_STORES' ? this.selectedShopIds : undefined,
      startAt: this.toLocalDateTime(this.startAt!),
      endAt: this.toLocalDateTime(this.endAt!),
      lines: validLines.map((line) => ({
        skuCode: line.skuCode!,
        skuName:
          this.skuOptions().find((o) => o.skuCode === line.skuCode)?.skuName ||
          line.skuName,
        amount: line.amount!,
        quota: line.quota!,
      })),
    };
    this.saving.set(true);
    this.bumpForm();
    const id = this.editingId();
    const req$ =
      id == null
        ? this.flashSaleService.createCampaign(body)
        : this.flashSaleService.updateCampaign(id, body);
    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogVisible.set(false);
        this.toast(
          'success',
          id == null ? 'flash_sale.messages.created' : 'flash_sale.messages.updated',
        );
        this.reload();
      },
      error: (err) => {
        this.saving.set(false);
        this.bumpForm();
        const raw = err?.error?.message || err?.error?.detail;
        this.toast('error', raw ? this.translateFlashSaleError(raw) : 'flash_sale.messages.save_failed');
      },
    });
  }

  remove(row: FlashSaleCampaign): void {
    this.flashSaleService
      .deleteCampaign(row.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast('success', 'flash_sale.messages.deleted');
          this.reload();
        },
        error: () => this.toast('error', 'flash_sale.messages.delete_failed'),
      });
  }

  alertDisplay(alert: FlashSaleAlert): string {
    return this.translate.instant('flash_sale.alerts.message', {
      name: alert.campaignName ?? '',
      when: this.formatAlertWhen(alert.startsAt),
    });
  }

  private formatAlertWhen(value?: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  quotaRemainingSummary(row: FlashSaleCampaign): string {
    const lines = row.lines ?? [];
    if (lines.length === 0) {
      return '—';
    }
    const remaining = lines.reduce(
      (sum, line) => sum + (Number(line.quotaRemaining ?? line.quota ?? 0) || 0),
      0,
    );
    return remaining.toLocaleString('vi-VN');
  }

  dismissAlert(alert: FlashSaleAlert): void {
    this.flashSaleService
      .markAlertRead(alert.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () =>
          this.alerts.update((list) => list.filter((item) => item.id !== alert.id)),
      });
  }

  scopeLabel(row: FlashSaleCampaign): string {
    if (row.scopeType === 'ALL_STORES_AND_WEB') {
      return this.translate.instant('price_list.scope.all_stores_and_web');
    }
    const names = (row.stores ?? []).map((s) => s.physicalShopName).filter(Boolean);
    return names.length
      ? names.join(', ')
      : this.translate.instant('flash_sale.scope.selected');
  }

  skuSummary(row: FlashSaleCampaign): string {
    const codes = row.skuCodes ?? row.lines?.map((l) => l.skuCode) ?? [];
    if (codes.length <= 3) {
      return codes.join(', ') || '—';
    }
    return `${codes.slice(0, 3).join(', ')} +${codes.length - 3}`;
  }

  scheduleSeverity(
    status: string,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case 'EFFECTIVE':
        return 'success';
      case 'SCHEDULED':
        return 'info';
      default:
        return 'secondary';
    }
  }

  /** Client-side schedule from window — updates with the live clock (no refresh). */
  liveScheduleStatus(row: FlashSaleCampaign): string {
    this.nowMs();
    const now = Date.now();
    const startMs = row.startAt ? new Date(row.startAt).getTime() : NaN;
    const endMs = row.endAt ? new Date(row.endAt).getTime() : NaN;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return row.scheduleStatus;
    }
    if (now < startMs) {
      return 'SCHEDULED';
    }
    if (now <= endMs) {
      return 'EFFECTIVE';
    }
    return 'EXPIRED';
  }

  liveCountdown(row: FlashSaleCampaign): string {
    this.nowMs();
    const now = Date.now();
    const startMs = row.startAt ? new Date(row.startAt).getTime() : NaN;
    const endMs = row.endAt ? new Date(row.endAt).getTime() : NaN;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return '—';
    }
    if (now < startMs) {
      return this.formatCountdown(Math.floor((startMs - now) / 1000));
    }
    if (now <= endMs) {
      return this.formatCountdown(Math.floor((endMs - now) / 1000));
    }
    return '—';
  }

  formatCountdown(seconds: number | null | undefined): string {
    if (seconds == null || seconds < 0) {
      return '—';
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  private loadOptions(): void {
    this.flashSaleService
      .effectiveSkus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (skus) => this.skuOptions.set(skus),
        error: () => this.skuOptions.set([]),
      });
    this.flashSaleService
      .physicalShops()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (shops) => this.shopOptions.set(shops),
        error: () => this.shopOptions.set([]),
      });
  }

  private refreshStock(): void {
    const codes = this.lines
      .map((line) => line.skuCode)
      .filter((code): code is string => !!code);
    if (codes.length === 0) {
      return;
    }
    if (
      this.scopeType === 'SELECTED_STORES' &&
      (!this.selectedShopIds || this.selectedShopIds.length === 0)
    ) {
      return;
    }
    this.flashSaleService
      .previewStock(
        this.scopeType,
        this.scopeType === 'SELECTED_STORES' ? this.selectedShopIds : undefined,
        codes,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preview) => {
          this.maxStockPercent.set(preview.maxStockPercent ?? 50);
          const bySku = new Map(
            (preview.lines ?? []).map((line) => [line.skuCode, line]),
          );
          this.lines = this.lines.map((line) => {
            if (!line.skuCode) {
              return line;
            }
            const stock = bySku.get(line.skuCode);
            return {
              ...line,
              currentStock: stock?.currentStock ?? 0,
              maxAllowedQuota: stock?.maxAllowedQuota ?? 0,
            };
          });
          this.bumpForm();
        },
      });
  }

  private resetForm(): void {
    const now = new Date();
    now.setSeconds(0, 0);
    this.campaignName = '';
    this.scopeType = 'ALL_STORES_AND_WEB';
    this.selectedShopIds = [];
    this.startAt = now;
    this.endAt = null;
    this.lines = [];
  }

  private toLocalDateTime(value: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}:00`;
  }

  private translateFlashSaleError(message: string): string {
    const overlap = message.match(
      /^(.*) already belongs to flash sale "(.*) \((.*)\)" at (.*)$/,
    );
    if (overlap) {
      return this.translate.instant('flash_sale.messages.overlap', {
        sku: overlap[1],
        campaign: overlap[2],
        window: overlap[3],
        place: this.translateFlashSalePlace(overlap[4]),
      });
    }
    const quota = message.match(
      /^Quota for (.+) exceeds (.+)% of current stock \((.+)\)$/,
    );
    if (quota) {
      return this.translate.instant('flash_sale.messages.quota_exceeds', {
        sku: quota[1],
        percent: quota[2],
        max: quota[3],
      });
    }
    if (message === 'Flash sale end must be after start') {
      return 'flash_sale.messages.end_before_start';
    }
    if (message === 'Flash sale start must not be in the past') {
      return 'flash_sale.messages.start_not_past';
    }
    if (message === 'Select at least one physical store') {
      return 'flash_sale.messages.stores_required';
    }
    if (message === 'At least one SKU is required') {
      return 'flash_sale.messages.required';
    }
    return message;
  }

  private translateFlashSalePlace(place: string): string {
    if (place === 'all stores and the website') {
      return this.translate.instant('flash_sale.messages.place_all');
    }
    if (place === 'the selected stores') {
      return this.translate.instant('flash_sale.messages.place_selected');
    }
    if (place.startsWith('stores ')) {
      return this.translate.instant('flash_sale.messages.place_stores', {
        names: place.slice('stores '.length),
      });
    }
    if (place.startsWith('store ')) {
      return this.translate.instant('flash_sale.messages.place_store', {
        name: place.slice('store '.length),
      });
    }
    return place;
  }

  private toast(severity: 'success' | 'info' | 'warn' | 'error', keyOrMessage: string): void {
    const detail = keyOrMessage.startsWith('flash_sale.')
      ? this.translate.instant(keyOrMessage)
      : keyOrMessage;
    this.messageService.add({
      severity,
      summary: this.translate.instant('flash_sale.title'),
      detail,
    });
  }
}
